import {useEffect, useMemo, useState} from 'react'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {Badge, Button, Col, FloatingLabel, Form, Offcanvas, Row, Spinner} from 'react-bootstrap'
import {Link} from 'react-router-dom'
import {FormPersonalLead} from '../FormPersonalLead'
import {FormComercialLead} from '../FormComercialLead'
import Select, {MultiValue} from 'react-select'
import {IResCatLeadSource} from '../../../leadSource/leadSourceTypes'
import {IResPipelines} from '../../../../types/pipelines'
import {IResTags} from '../../../../types/tags'
import {IResCampus} from '../../../../types/campus'
import {formatUserOptionLabel, hasPermission, parseId} from '../../../../helpers'
import {IResAllListUsers} from '../../../../types/users'
import {ILeadData, IResUpdateLead, LeadByID, UpdatedLeadNode} from '../../leadTypes'
import {IResAllReasons} from '../../../../types/reasonLoss'
import {useMutation} from '@apollo/client'
import {UPDATE_LEAD} from '../../../../gql/mutations/leadMutations'
import {toast} from 'react-toastify'
import {useGetUser} from '../../../../store/userStore'
import {ALL_PERMISSIONS} from '../../../../helpers/consts'
import {PhoneInput} from 'app/tfi/components/PhoneInput'
import {DateInput} from '../../../../components/DateInput'
import moment from 'moment'
import {EZLynxSyncBadge} from '../EZLynxSyncBadge'
import {getLeadSourceIcon} from '../../helpers/leadSourceIcon'
import {LovableIcon} from '../../../../components/icons/LovableIcon'

interface IFormEditProps {
  dataLeadSource: IResCatLeadSource
  dataPipelines: IResPipelines
  dataTags: IResTags
  dataCampus: IResCampus
  dataUserList: IResAllListUsers
  onClose: () => void
  updateListLeads: () => void
  onLeadUpdated?: (lead: UpdatedLeadNode) => void
  lead: LeadByID
  dataReasonLoss: IResAllReasons
  children: JSX.Element
}

const requiredMark = '*'
const colon = ':'
const dash = '-'
const FB_RESERVED_TOP_KEYS = new Set([
  'source',
  'leadgen_id',
  'extra_fields',
  'form_id',
  'campaign_id',
  'webhook_description',
])
// 'facebook_ads' se mantiene por compatibilidad con leads creados antes del rename a
// Zapier — el webhook nunca fue exclusivo de Facebook, solo se llamaba así.
const WEBHOOK_SOURCES = new Set(['zapier', 'facebook_ads', 'lovable', 'google_ads'])

const eM = defineMessages({
  update: {id: 'common.action.update', defaultMessage: 'Update'},
  mpSourceSuffix: {id: 'leads.edit.marketplace.sourceSuffix', defaultMessage: ' ({description})'},
  toastUpdated: {id: 'leads.edit.toast.updated', defaultMessage: 'Lead updated successfully'},
  mpDetails: {id: 'leads.edit.marketplace.details', defaultMessage: 'Lead details'},
  mpType: {id: 'common.field.type', defaultMessage: 'Type'},
  mpPlan: {id: 'leads.edit.marketplace.plan', defaultMessage: 'Plan'},
  mpEffectiveDate: {id: 'leads.edit.marketplace.effectiveDate', defaultMessage: 'Effective date'},
  mpCarrier: {id: 'leads.edit.marketplace.carrier', defaultMessage: 'Carrier'},
  mpMembers: {id: 'common.field.members', defaultMessage: 'Members'},
  mpPropertyType: {id: 'leads.edit.marketplace.propertyType', defaultMessage: 'Property type'},
  mpDamageType: {id: 'leads.edit.marketplace.damageType', defaultMessage: 'Damage type'},
  fbDetails: {id: 'leads.edit.zapier.details', defaultMessage: 'Zapier webhook details'},
  lovableDetails: {id: 'leads.edit.lovable.details', defaultMessage: 'Lovable form details'},
  googleAdsDetails: {id: 'leads.edit.googleAds.details', defaultMessage: 'Google Ads webhook details'},
  fbFormId: {id: 'leads.edit.facebook.formId', defaultMessage: 'Form ID'},
  fbCampaignId: {id: 'leads.edit.facebook.campaignId', defaultMessage: 'Campaign ID'},
})

export const FormEditLead = ({
  dataCampus,
  dataLeadSource,
  dataPipelines,
  dataTags,
  dataUserList,
  onClose,
  lead,
  dataReasonLoss,
  updateListLeads,
  onLeadUpdated,
  children,
}: IFormEditProps) => {
  const intl = useIntl()
  const na = intl.formatMessage({id: 'common.value.notAvailable', defaultMessage: 'N/A'})
  const errMsg = (message?: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})
  const permissions = useGetUser((s) => s.user?.permissions)
  const canEditLeadPersonalPhone =
    !!permissions && hasPermission(permissions, ALL_PERMISSIONS.EDIT_LEAD_PERSONAL)
  const [gqlEdit, {data: dataEdit, loading: loadingEdit, error: errorEdit}] =
    useMutation<IResUpdateLead>(UPDATE_LEAD)

  // TODO: getQuoteByLeadEdit rompe con "Invalid ID specified." cuando parseId(lead.id)
  // no resuelve a un ID numérico válido para el filtro `lead` de allQuotes. Deshabilitado
  // temporalmente hasta resolverlo.
  // const GET_QUOTE_BY_LEAD = useMemo(
  //   () => gql`
  //     query getQuoteByLeadEdit($lead: ID) {
  //       allQuotes(lead: $lead, first: 1) {
  //         edges {
  //           node {
  //             id
  //             planName
  //             status
  //           }
  //         }
  //       }
  //     }
  //   `,
  //   []
  // )

  // const leadPk = parseId(lead.id)
  // const {data: quoteData} = useQuery(GET_QUOTE_BY_LEAD, {
  //   variables: {lead: leadPk},
  //   skip: !leadPk,
  //   fetchPolicy: 'cache-first',
  // })

  // const quoteId = quoteData?.allQuotes?.edges?.[0]?.node?.id
  const quoteId = undefined

  const [leadType, setLeadType] = useState<'PERSONAL' | 'COMERCIAL'>('PERSONAL')
  const [valuePipelines, setValuePipelines] = useState('')
  const [stageValue, setStageValue] = useState('')
  const [birthDay, setBirthDay] = useState<string>('')
  const [valueTags, setValueTags] = useState<
    MultiValue<{
      value: string
      label: string
      color: string
    }>
  >([])
  const [valueMembers, setValueMembers] = useState<
    MultiValue<{
      value: string
      label: string
    }>
  >([])

  const marketplaceData = useMemo<ApplicationData | null>(() => {
    const metadata = lead?.marketplaceMetadata

    if (!metadata) return null
// console.log(' marketplace metadata', metadata);
    try {
      if (typeof metadata === 'string') {
        const parsed = JSON.parse(metadata)
        // console.log('parsed marketplace metadata', parsed);
        if (parsed && typeof parsed === 'object') {
          return parsed as ApplicationData
        }
        return null
      }
      return metadata as ApplicationData
    } catch (error) {
      console.warn('Unable to parse marketplace metadata', error)
      return null
    }
  }, [lead?.marketplaceMetadata])

  useEffect(() => {
    if (!lead) return
    const tLead = lead.comercialLead ? 'COMERCIAL' : lead.personalLead ? 'PERSONAL' : null
    if (!tLead) return
    setLeadType(tLead)
    const tags = lead.tagInsurance.edges.map(({node}) => ({
      value: node.id,
      label: node.name,
      color: node.color,
    }))
    setValueTags(tags)
    setValuePipelines(`${parseId(lead.stagePipeline.pipeline.id)}`)
    const mems = lead.user.edges.map(({node}) => ({
      value: node.id,
      label: formatUserOptionLabel(node),
    }))
    setValueMembers(mems)
    setStageValue(`${parseId(lead.stagePipeline.id)}`)
    if (lead.personalLead?.birthDay) {
      setBirthDay(lead.personalLead.birthDay)
    }
  }, [lead])

  const defaultCountryCode = useMemo(() => {
    if (!lead) return '1'
    
    // Check personal lead country
    const personalCountry = lead.personalLead?.country?.toLowerCase() || ''
    if (personalCountry.includes('panama') || personalCountry.includes('panamá') || personalCountry === 'pa') return '507'
    if (personalCountry.includes('mexico') || personalCountry.includes('méxico') || personalCountry === 'mx') return '52'
    if (personalCountry.includes('colombia') || personalCountry === 'co') return '57'
    
    // Check campus name as a fallback hint
    const campusName = lead.campus?.name?.toLowerCase() || ''
    if (campusName.includes('panama') || campusName.includes('panamá')) return '507'
    
    return '1'
  }, [lead])

  useEffect(() => {
    if (errorEdit) toast.error(errMsg(errorEdit.message))
  }, [errorEdit])

  useEffect(() => {
    if (!dataEdit) return
    if (dataEdit.updateLead.success) {
      toast.success(intl.formatMessage(eM.toastUpdated))
      onClose()
      if (dataEdit.updateLead.lead && onLeadUpdated) {
        onLeadUpdated(dataEdit.updateLead.lead)
      } else {
        updateListLeads()
      }
    } else if (dataEdit.updateLead.errors) {
      toast.error(errMsg(dataEdit.updateLead.errors?.[0]?.message || intl.formatMessage({id: 'leads.add.toast.somethingHappened', defaultMessage: 'something happened'})))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataEdit])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const {
      name,
      email,
      phone,
      leadSource,
      pipeline,
      campus,
      reasonLeadLoss,
      tags,
      observations,
      stagePipeline,
      ...rest
    } = Object.fromEntries(new FormData(e.currentTarget))
    const leadData: ILeadData = {
      name,
      email,
      phone,
      typeLead: leadType,
      leadSource,
      pipeline,
      campus,
      reasonLeadLoss: reasonLeadLoss.toString() ? reasonLeadLoss : undefined,
      tags,
      observations,
      stagePipeline,
    }
    if (valueTags.length > 0) {
      leadData.tagInsurance = valueTags.map(({value}) => parseId(value))
    }
    leadData.user = valueMembers.map(({value}) => parseId(value))
    if (leadType === 'PERSONAL') {
      leadData.personalLead = {...rest}
      if (!leadData.personalLead.birthDay) {
        delete leadData.personalLead.birthDay
      }
      delete leadData.comercialLead
    }
    if (leadType === 'COMERCIAL') {
      leadData.comercialLead = {...rest}
      delete leadData.personalLead
    }
    delete leadData.tags
    delete leadData.pipeline
    console.log('leadData', leadData)
    gqlEdit({
      variables: {
        id: lead.id,
        leadData,
      },
    })
  }

  return (
    <>
      <Offcanvas.Header closeButton className='bg-light'>
        <div className='d-flex align-items-center justify-content-between w-100'>
          <Offcanvas.Title className='d-flex align-items-center gap-3'>
            {lead.name}
            <EZLynxSyncBadge leadId={lead.id} showButton={true} />
          </Offcanvas.Title>
          <div style={{flex: 0}}>
            <Badge bg='success' pill className='fs-2 d-flex align-items-center gap-1'>
              <i className='bi bi-telephone-plus' style={{color: 'inherit'}} />
              {lead.callCounter}
            </Badge>
          </div>
          <div className='d-flex align-items-center gap-1'>
            <button
              className=''
              onClick={() => {
                setLeadType('PERSONAL')
              }}
              style={{
                border: 'none',
                padding: '4px 10px',
                borderRadius: '14px',
                color: leadType === 'PERSONAL' ? 'white' : 'gray',
                backgroundColor: leadType === 'PERSONAL' ? '#3e97ff' : '#e4e4e4',
              }}
            >
              <FormattedMessage id='leads.form.typePersonal' defaultMessage='Personal' />
            </button>
            <button
              className=''
              onClick={() => {
                setLeadType('COMERCIAL')
              }}
              style={{
                border: 'none',
                padding: '4px 10px',
                borderRadius: '14px',
                color: leadType === 'COMERCIAL' ? 'white' : 'gray',
                backgroundColor: leadType === 'COMERCIAL' ? '#3e97ff' : '#e4e4e4',
              }}
            >
              <FormattedMessage id='leads.form.typeCommercial' defaultMessage='Commercial' />
            </button>
          </div>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {children}
        <form onSubmit={handleSubmit}>
          <div className='lead-summary-layout'>
            <div className='lead-summary lead-summary-sticky'>
              <div className='kt-heading kt-heading--sm custom-border-brand my-3'>{intl.formatMessage({id: 'leads.list.summary.title', defaultMessage: 'Lead summary'})}</div>
              <Form.Group className='mb-3'>
                <Form.Label>
                  <FormattedMessage id='leads.list.pipeline' defaultMessage='Pipeline' /> <span style={{color: 'red'}}>{requiredMark}</span>
                </Form.Label>
                <Form.Select
                  aria-label={intl.formatMessage({id: 'leads.list.pipeline', defaultMessage: 'Pipeline'})}
                  name='pipeline'
                  required
                  size='sm'
                  value={valuePipelines}
                  onChange={(e) => setValuePipelines(e.target.value)}
                >
                  <option value=''>{intl.formatMessage({id: 'leads.form.nothingSelected', defaultMessage: 'Nothing selected'})}</option>
                  {dataPipelines.allPipelines.edges.map(({node}) => (
                    <option value={parseId(node.id)} key={node.id}>
                      {node.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>
                  <FormattedMessage id='leads.list.summary.stage' defaultMessage='Stage' /> <span style={{color: 'red'}}>{requiredMark}</span>
                </Form.Label>
                <Form.Select
                  aria-label={intl.formatMessage({id: 'leads.list.summary.stage', defaultMessage: 'Stage'})}
                  name='stagePipeline'
                  required
                  size='sm'
                  value={stageValue}
                  onChange={(e) => setStageValue(e.target.value)}
                >
                  <option value=''>{intl.formatMessage({id: 'leads.form.nothingSelected', defaultMessage: 'Nothing selected'})}</option>
                  {dataPipelines.allPipelines.edges
                    .find(({node}) => parseId(node.id) === valuePipelines)
                    ?.node.stagePipelineSet.edges.map(({node}) => (
                      <option key={node.id} value={parseId(node.id)}>
                        {node.name}
                      </option>
                    ))}
                </Form.Select>
                {quoteId && (
                  <div className='mt-1'>
                    <Link
                      to={`/one-quote/${quoteId}`}
                      className='btn btn-sm btn-light-primary w-100'
                      style={{fontSize: '0.75rem'}}
                    >
                      <i className='bi bi-box-arrow-up-right me-1' />
                      <FormattedMessage id='leads.list.summary.viewQuote' defaultMessage='View quote' />
                    </Link>
                  </div>
                )}
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>
                  <FormattedMessage id='leads.list.summary.source' defaultMessage='Source' /> <span style={{color: 'red'}}>{requiredMark}</span>
                </Form.Label>
                <Form.Select
                  aria-label={intl.formatMessage({id: 'leads.form.leadSource', defaultMessage: 'Lead source'})}
                  name='leadSource'
                  required
                  size='sm'
                  defaultValue={parseId(lead.leadSource.id)}
                >
                  <option value=''>{intl.formatMessage({id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'})}</option>
                  {dataLeadSource.allCategoryLeadSource.edges.map(({node}) => (
                    <optgroup label={node.name} key={node.id}>
                      {node.leadSourceSet.edges.map(({node: nodeLead}) => (
                        <option key={nodeLead.id} value={parseId(nodeLead.id)}>
                          {nodeLead.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className='mb-3 z-999'>
                <Form.Label>
                  <FormattedMessage id='leads.list.summary.tags' defaultMessage='Tags' />
                </Form.Label>
                <Select
                  classNames={{
                    control: () => 'h-100',
                    container: () => 'h-100',
                  }}
                  placeholder={intl.formatMessage({id: 'leads.form.selectTags', defaultMessage: 'Select tags'})}
                  id='tags-cont'
                  inputId='tags'
                  name='tags'
                  options={dataTags.allTagInsurances.edges.map(({node}) => ({
                    value: node.id,
                    label: node.name,
                    color: node.color,
                  }))}
                  isMulti
                  value={valueTags}
                  onChange={(e) => {
                    setValueTags(e)
                  }}
                  styles={{
                    option: (styles, {data, isDisabled, isFocused, isSelected}) => {
                      const color = data.color
                      return {
                        ...styles,
                        zIndex: 9999,
                        backgroundColor: isDisabled
                          ? undefined
                          : isSelected
                          ? data.color
                          : isFocused
                          ? color
                          : undefined,
                        color: isDisabled ? '#ccc' : isSelected ? 'white' : 'black',
                        cursor: isDisabled ? 'not-allowed' : 'default',
                        ':active': {
                          ...styles[':active'],
                          backgroundColor: !isDisabled
                            ? isSelected
                              ? data.color
                              : color
                            : undefined,
                        },
                      }
                    },
                    multiValue: (styles, {data}) => {
                      const color = data.color
                      return {
                        ...styles,
                        backgroundColor: color,
                      }
                    },
                    multiValueLabel: (styles) => ({
                      ...styles,
                      color: 'white',
                    }),
                    multiValueRemove: (styles, {data}) => ({
                      ...styles,
                      color: 'white',
                      ':hover': {
                        backgroundColor: data.color,
                        color: 'white',
                      },
                    }),
                    control: (styles, {isDisabled, isFocused}) => {
                      return {
                        ...styles,
                        minHeight: '36px',
                        border: isDisabled
                          ? '1px solid #ccc'
                          : isFocused
                          ? '1px solid white'
                          : '1px solid #dbdfe9',
                        cursor: isDisabled ? 'not-allowed' : 'default',
                      }
                    },
                  }}
                />
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>
                  <FormattedMessage id='leads.list.summary.reasonForLoss' defaultMessage='Reason for loss' /> <span style={{color: 'red'}}>{requiredMark}</span>
                </Form.Label>
                <Form.Select
                  aria-label={intl.formatMessage({id: 'leads.list.summary.reasonForLoss', defaultMessage: 'Reason for loss'})}
                  name='reasonLeadLoss'
                  size='sm'
                  defaultValue={
                    lead?.reasonLeadLoss?.id ? parseId(lead.reasonLeadLoss.id) : undefined
                  }
                >
                  <option value=''>{intl.formatMessage({id: 'leads.form.nothingSelected', defaultMessage: 'Nothing selected'})}</option>
                  {dataReasonLoss.allReasonLeadLoss.edges.map(({node}) => (
                    <option value={parseId(node.id)} key={node.id}>
                      {node.reason}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>
                  <FormattedMessage id='common.field.location' defaultMessage='Location' /> <span style={{color: 'red'}}>{requiredMark}</span>
                </Form.Label>
                <Form.Select
                  aria-label={intl.formatMessage({id: 'common.field.location', defaultMessage: 'Location'})}
                  name='campus'
                  required
                  size='sm'
                  defaultValue={parseId(lead.campus.id)}
                >
                  {dataCampus.allCampus.edges.map(({node}) => (
                    <option value={parseId(node.id)} key={node.id}>
                      {node.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className='mb-3 z-999'>
                <Form.Label><FormattedMessage id='leads.list.summary.assignedTo' defaultMessage='Assigned to' /></Form.Label>
                <Select
                  classNames={{
                    container: () => 'h-100',
                  }}
                  styles={{
                    control: (styles) => ({
                      ...styles,
                      minHeight: '36px',
                    }),
                  }}
                  placeholder={intl.formatMessage({id: 'leads.form.selectMembers', defaultMessage: 'Select members'})}
                  options={dataUserList.allUsersList.map((node) => ({
                    value: node.id,
                    label: formatUserOptionLabel(node),
                  }))}
                  isMulti
                  value={valueMembers}
                  onChange={(e) => {
                    setValueMembers(e)
                  }}
                />
              </Form.Group>
              <div className='kt-widget__info'>
                <span className='kt-widget__label'><FormattedMessage id='leads.list.summary.createdAt' defaultMessage='Created at' />{colon}</span>
                <span className='kt-widget__data'>
                  {lead.created ? moment(lead.created).format('DD MMM YYYY, hh:mm A') : na}
                </span>
              </div>
              <div className='kt-widget__info'>
                <span className='kt-widget__label'><FormattedMessage id='leads.list.summary.lastUpdate' defaultMessage='Last update' />{colon}</span>
                <span className='kt-widget__data'>
                  {lead.updated ? moment(lead.updated).format('DD MMM YYYY, hh:mm A') : na}
                </span>
              </div>
            </div>
            <div className='lead-summary-content'>
              <Row>
                {/* Personal Information | Address Information */}
                <Col className='col-lg-8 col-md-8 col-12 holder-division'>
                  <div className='kt-heading kt-heading--md custom-border-brand mb-3'>{intl.formatMessage({id: 'leads.list.summary.personalInfo', defaultMessage: 'Personal information'})}</div>
                  <Row>
                    {/* Lead Name | Email | Civil Status */}
                    <Col className='col-12 mb-4'>
                      <Form.Group>
                        <Form.Label>
                          <FormattedMessage id='leads.form.leadName' defaultMessage='Lead name' /> <span style={{color: 'red'}}>{requiredMark}</span>
                        </Form.Label>
                        <Form.Control
                          placeholder={intl.formatMessage({id: 'leads.form.leadName', defaultMessage: 'Lead name'})}
                          name='name'
                          required
                          defaultValue={lead.name}
                        />
                      </Form.Group>
                    </Col>
                    {leadType === 'PERSONAL' && (
                      <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                        <DateInput
                          id='birthDay'
                          name='birthDay'
                          label={
                            <>
                              <span><FormattedMessage id='common.field.dateOfBirth' defaultMessage='Date of Birth' /></span>
                              <span className='age'>
                                {birthDay
                                  ? intl.formatMessage({id: 'leads.form.age', defaultMessage: 'Age: {age}'}, {age: moment().diff(moment(birthDay), 'years')})
                                  : intl.formatMessage({id: 'leads.form.ageEmpty', defaultMessage: 'Age: -'})}
                              </span>
                            </>
                          }
                          labelClassName='label-date-of-birth-age d-flex justify-content-between align-items-center w-100'
                          placeholder={intl.formatMessage({id: 'common.field.dateOfBirth', defaultMessage: 'Date of Birth'})}
                          value={birthDay}
                          onChange={(_, value) => setBirthDay(value || '')}
                        />
                        <input type='hidden' name='birthDay' value={birthDay} />
                      </Col>
                    )}
                    {leadType === 'PERSONAL' && (
                      <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                        <Form.Group>
                          <Form.Label>{intl.formatMessage({id: 'leads.form.civilStatus', defaultMessage: 'Civil status'})}</Form.Label>
                          <Form.Control
                            name='civilStatus'
                            type='text'
                            placeholder={intl.formatMessage({id: 'leads.form.civilStatus', defaultMessage: 'Civil status'})}
                            defaultValue={lead.personalLead?.civilStatus || undefined}
                          />
                        </Form.Group>
                      </Col>
                    )}

                    {/* Email | Phone */}
                    <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                      <Form.Group>
                        <Form.Label>
                          <FormattedMessage id='common.field.email' defaultMessage='Email' /> <span style={{color: 'red'}}>{requiredMark}</span>
                        </Form.Label>
                        <Form.Control
                          placeholder={intl.formatMessage({id: 'common.field.email', defaultMessage: 'Email'})}
                          name='email'
                          required
                          defaultValue={lead.email}
                        />
                      </Form.Group>
                    </Col>
                    <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                      <Form.Group>
                        <PhoneInput
                          id='phone'
                          name='phone'
                          label={intl.formatMessage({id: 'common.field.phone', defaultMessage: 'Phone'})}
                          value={lead.phone ?? ''}
                          defaultCountryCode={defaultCountryCode}
                          readOnly={!canEditLeadPersonalPhone}
                          onChange={() => {}}
                        />
                      </Form.Group>
                    </Col>

                    <Col className='col-12 mb-4'>
                      <FloatingLabel controlId='observations' label={intl.formatMessage({id: 'leads.form.observations', defaultMessage: 'Observations'})}>
                        <Form.Control
                          defaultValue={lead?.observations || ''}
                          name='observations'
                          as='textarea'
                          rows={3}
                          placeholder={intl.formatMessage({id: 'leads.form.observations', defaultMessage: 'Observations'})}
                          className='h-100'
                        />
                      </FloatingLabel>
                    </Col>
                  </Row>
                </Col>

                {leadType === 'PERSONAL' && (
                  <Col className='col-lg-4 col-md-4 col-12'>
                    <div className='kt-heading kt-heading--md custom-border-brand mb-3'>{intl.formatMessage({id: 'leads.form.addressInfo', defaultMessage: 'Address information'})}</div>
                    <FormPersonalLead info={lead.personalLead} />
                  </Col>
                )}
                {leadType === 'COMERCIAL' && <FormComercialLead info={lead.comercialLead} />}
                {marketplaceData && !WEBHOOK_SOURCES.has(marketplaceData.source as string) && (
                  <Col className='col-12 mb-4'>
                    {marketplaceData && (
                      <div className='alert alert-info d-flex flex-column gap-1 mb-4'>
                        <div className='d-flex align-items-center justify-content-between'>
                          <strong>
                            {intl.formatMessage(eM.mpDetails)}
                            {typeof marketplaceData.source === 'object' && marketplaceData.source?.description
                              ? intl.formatMessage(eM.mpSourceSuffix, {description: marketplaceData.source.description})
                              : ''}
                          </strong>
                          {quoteId && (
                            <Link
                              to={`/one-quote/${quoteId}`}
                              className='btn btn-sm btn-primary py-1 px-2'
                              style={{fontSize: '0.75rem', whiteSpace: 'nowrap'}}
                            >
                              <i className='bi bi-box-arrow-up-right me-1' />
                              <FormattedMessage id='leads.list.summary.viewQuote' defaultMessage='View quote' />
                            </Link>
                          )}
                        </div>
                        
                        {marketplaceData.household?.type && (
                          <span>
                           {intl.formatMessage(eM.mpType)}{colon}&nbsp;{marketplaceData.household.type}
                          </span>
                        )}

                        {marketplaceData.plan?.selection?.planId && marketplaceData.plan?.selection?.name && (
                           <span>
                           {intl.formatMessage(eM.mpPlan)}{colon}&nbsp;
                           {marketplaceData.plan.selection.planId} {dash} {marketplaceData.plan.selection.name}
                         </span>
                        )}

                        {marketplaceData.household?.effectiveDate && (
                           <span>
                           {intl.formatMessage(eM.mpEffectiveDate)}{colon} {marketplaceData.household.effectiveDate}
                         </span>
                        )}

                        {marketplaceData.plan?.selection?.issuer && (
                          <span>{intl.formatMessage(eM.mpCarrier)}{colon} {marketplaceData.plan.selection.issuer}</span>
                        )}

                        {marketplaceData.members?.length > 0 && (
                          <div>
                            <strong>{intl.formatMessage(eM.mpMembers)}{colon}</strong>
                            <ul className='mb-0'>
                              {marketplaceData.members.map((member, index) => (
                                <li key={index}>
                                  {member.firstName} {member.lastName} {dash} {member.birthDate} {dash} {member.gender}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {marketplaceData.property_type && (
                          <span>
                            <strong>{intl.formatMessage(eM.mpPropertyType)}{colon}</strong> {marketplaceData.property_type}
                          </span>
                        )}
                        {marketplaceData.damage_type && (
                          <span>
                            <strong>{intl.formatMessage(eM.mpDamageType)}{colon}</strong> {marketplaceData.damage_type}
                          </span>
                        )}
                      </div>
                    )}
                  </Col>
                )}
                {marketplaceData && WEBHOOK_SOURCES.has(marketplaceData.source as string) && (
                  <Col className='col-12 mb-4'>
                    <div className='alert alert-info d-flex flex-column gap-1 mb-4'>
                      <strong>
                        {marketplaceData.source === 'lovable' ? (
                          <LovableIcon size={16} className='me-1' />
                        ) : (
                          <i
                            className={`bi ${
                              getLeadSourceIcon(lead.leadSource?.name) ||
                              (marketplaceData.source === 'google_ads' ? 'bi-google' : 'bi-lightning-charge')
                            } me-1`}
                          />
                        )}
                        {intl.formatMessage(
                          marketplaceData.source === 'lovable'
                            ? eM.lovableDetails
                            : marketplaceData.source === 'google_ads'
                              ? eM.googleAdsDetails
                              : eM.fbDetails
                        )}
                      </strong>

                      {marketplaceData.webhook_description && (
                        <span className='fst-italic'>{marketplaceData.webhook_description}</span>
                      )}

                      {marketplaceData.form_id && (
                        <span>
                          {intl.formatMessage(eM.fbFormId)}
                          {colon}&nbsp;{marketplaceData.form_id}
                        </span>
                      )}

                      {marketplaceData.campaign_id && (
                        <span>
                          {intl.formatMessage(eM.fbCampaignId)}
                          {colon}&nbsp;{marketplaceData.campaign_id}
                        </span>
                      )}

                      {/* Fields resolved via the webhook's configured metadata field map
                          (target name -> Zapier key) land as top-level keys, not in extra_fields */}
                      {Object.entries(marketplaceData as unknown as Record<string, unknown>)
                        .filter(
                          ([key, value]) =>
                            !FB_RESERVED_TOP_KEYS.has(key) && value !== null && value !== undefined && value !== ''
                        )
                        .map(([key, value]) => (
                          <span key={key}>
                            <strong>{key}</strong>
                            {colon}&nbsp;{String(value)}
                          </span>
                        ))}

                      {marketplaceData.extra_fields && Object.keys(marketplaceData.extra_fields).length > 0 && (
                        <ul className='mb-0 ps-3'>
                          {Object.entries(marketplaceData.extra_fields).map(([key, value]) => (
                            <li key={key}>
                              <strong>{key}</strong>
                              {colon}&nbsp;{String(value)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </Col>
                )}
              </Row>

              <div
                className='d-flex align-items-center justify-content-end gap-2 mt-8 pt-3 pb-8'
                style={{borderTop: '1px solid #f1f1f1'}}
              >
                <Button type='button' variant='light' onClick={onClose}>
                  <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
                </Button>
                {permissions && hasPermission(permissions, ALL_PERMISSIONS.EDIT_LEAD) && (
                  <Button type='submit' disabled={loadingEdit}>
                    {loadingEdit && <Spinner animation='border' size='sm' />}
                    {intl.formatMessage(eM.update)}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </form>
      </Offcanvas.Body>
    </>
  )
}
